import React, { useEffect, useState } from "react";
import { Layout, Row, Col, Card, Button, Typography, Select } from "antd";
import { Link } from "react-router-dom";
import { Header, Footer } from "components/Layout";
import { API_CONFIG } from "./config";
import { apiFetch } from "../utils/apiFetch";

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { Option } = Select;

export default function Landing() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(() => {
    return Number(localStorage.getItem("tenantId") || localStorage.getItem("mockdb:tenantId") || 1);
  });

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const res = await apiFetch(API_CONFIG.tenantsListUrl, { method: "POST" });
        const data = await res.json();
        setTenants(data.tenants || []);
      } catch {
        setTenants([]);
      }
    };
    loadTenants();
  }, []);

  const activeTenant = tenants.find((t) => t.id === tenantId) || tenants[0];
  const apps = activeTenant?.apps || ["indigent", "lending", "leads"];

  const handleTenantChange = (value) => {
    setTenantId(value);
    localStorage.setItem("tenantId", String(value));
    localStorage.setItem("mockdb:tenantId", String(value));
  };

  return (
    <div className="layout-default layout-landing">
      <Header btn="primary" />
      <Content className="landing-content">
        <section className="landing-hero">
          <div className="hero-inner">
            <div className="hero-text">
              <Title level={1}>Unified Social Support and Micro-Lending Platform</Title>
              <Paragraph className="hero-sub">
                One system for indigent management, identity verification, and lending applications.
                Built for front desks, applicants, reviewers, and disbursement teams.
              </Paragraph>
              <div style={{ marginBottom: 14 }}>
                <Select
                  value={activeTenant?.id}
                  onChange={handleTenantChange}
                  placeholder="Select tenant"
                  style={{ minWidth: 240 }}
                >
                  {tenants.map((tenant) => (
                    <Option key={tenant.id} value={tenant.id}>{tenant.name}</Option>
                  ))}
                </Select>
              </div>
              <div className="hero-cta">
                {apps.includes("lending") && (
                  <Link to="/apply?product=lending">
                    <Button type="primary" size="large" className="cta-primary">Apply for a Loan</Button>
                  </Link>
                )}
                {apps.includes("indigent") && (
                  <Link to="/apply?product=indigent">
                    <Button size="large" className="cta-secondary">Apply for Indigent Support</Button>
                  </Link>
                )}
              </div>
            </div>
            <div className="hero-panel">
              <div className="hero-card">
                <div className="hero-card-title">Live Workflow</div>
                <div className="hero-card-body">
                  Capture applications, run checks, approve, and disburse funds with audit-grade tracking.
                </div>
                <div className="hero-card-meta">
                  Secure, role-based, multi-country ready.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section">
          <Row gutter={[24, 24]}>
            {apps.includes("indigent") && (
              <Col xs={24} md={8}>
                <Card className="feature-card" bordered={false}>
                  <div className="feature-title">Front Desk</div>
                  <div className="feature-body">Capture indigent applications on behalf of applicants, upload documents, and submit.</div>
                </Card>
              </Col>
            )}
            {apps.includes("leads") && (
              <Col xs={24} md={8}>
                <Card className="feature-card" bordered={false}>
                  <div className="feature-title">Leads & Bookings</div>
                  <div className="feature-body">Manage leads, booking flows, and invoicing pipelines.</div>
                </Card>
              </Col>
            )}
            {apps.includes("lending") && (
              <Col xs={24} md={8}>
                <Card className="feature-card" bordered={false}>
                  <div className="feature-title">Applicants</div>
                  <div className="feature-body">Self-apply online, track status, and respond to requests for information.</div>
                </Card>
              </Col>
            )}
          </Row>
        </section>

        <section className="landing-section access-grid">
          <Title level={3} className="access-title">Choose your access</Title>
          <Row gutter={[24, 24]}>
            {apps.includes("lending") && (
              <Col xs={24} md={8}>
                <Card className="access-card" bordered={false}>
                  <div className="access-card-title">Applicant Portal</div>
                  <div className="access-card-body">Start a new application and upload required documents.</div>
                  <Link to="/apply">
                    <Button type="primary" block className="cta-primary">Open Applicant Portal</Button>
                  </Link>
                </Card>
              </Col>
            )}
            {apps.includes("indigent") && (
              <Col xs={24} md={8}>
                <Card className="access-card" bordered={false}>
                  <div className="access-card-title">Front Desk / Staff</div>
                  <div className="access-card-body">Capture applications on behalf of applicants.</div>
                  <Link to="/sign-in?redirect=/search">
                    <Button block className="cta-secondary">Sign In</Button>
                  </Link>
                </Card>
              </Col>
            )}
            {apps.includes("leads") && (
              <Col xs={24} md={8}>
                <Card className="access-card" bordered={false}>
                  <div className="access-card-title">Back Office / Admin</div>
                  <div className="access-card-body">Review, approve, and manage users and disbursements.</div>
                  <Link to="/sign-in?redirect=/admin">
                    <Button block className="cta-secondary">Sign In</Button>
                  </Link>
                </Card>
              </Col>
            )}
          </Row>
        </section>
      </Content>
      <Footer />

      <style>{`
        .layout-landing .landing-content {
          padding: 0 24px 48px;
          background:
            radial-gradient(1200px 700px at -10% -20%, rgba(253,224,71,0.18), transparent 60%),
            radial-gradient(900px 600px at 110% -30%, rgba(251,191,36,0.12), transparent 60%),
            linear-gradient(180deg, #0b0b12 0%, #0f1117 55%, #0b0b12 100%);
          color: #f8fafc;
        }
        .landing-hero {
          padding: 48px 0 32px;
        }
        .hero-inner {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 28px;
          align-items: center;
        }
        .hero-text h1 {
          color: #f8fafc;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .hero-sub {
          color: rgba(248,250,252,0.75);
          font-size: 16px;
          max-width: 680px;
        }
        .hero-cta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 18px;
        }
        .cta-primary {
          background: linear-gradient(90deg, #facc15 0%, #eab308 100%);
          border: none;
          color: #111;
          font-weight: 700;
          box-shadow: 0 10px 24px rgba(234,179,8,0.25);
        }
        .cta-secondary {
          background: transparent;
          border: 1px solid rgba(253,224,71,0.6);
          color: #fff8dc;
        }
        .hero-panel .hero-card {
          background: linear-gradient(180deg, rgba(2,6,23,0.9), rgba(2,6,23,0.7));
          border: 1px solid rgba(253,224,71,0.35);
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 16px 36px rgba(0,0,0,0.35);
        }
        .hero-card-title {
          color: #fde68a;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .hero-card-body {
          color: #e5e7eb;
        }
        .hero-card-meta {
          margin-top: 12px;
          color: rgba(253,224,71,0.7);
          font-size: 12px;
        }
        .landing-section {
          margin-top: 32px;
        }
        .feature-card {
          background: linear-gradient(180deg, rgba(17,24,39,0.85), rgba(17,24,39,0.7));
          border: 1px solid rgba(253,224,71,0.25);
          border-radius: 14px;
          color: #e5e7eb;
          min-height: 140px;
        }
        .feature-title {
          font-weight: 700;
          color: #fde68a;
          margin-bottom: 6px;
        }
        .access-grid .access-title {
          color: #f8fafc;
          margin-bottom: 12px;
        }
        .access-card {
          background: linear-gradient(180deg, rgba(2,6,23,0.85), rgba(2,6,23,0.65));
          border: 1px solid rgba(253,224,71,0.28);
          border-radius: 14px;
          color: #e5e7eb;
        }
        .access-card-title {
          font-weight: 700;
          color: #fde68a;
          margin-bottom: 6px;
        }
        .access-card-body {
          margin-bottom: 14px;
          color: rgba(248,250,252,0.7);
        }
        @media (max-width: 640px) {
          .layout-landing .landing-content { padding: 0 16px 40px; }
        }
      `}</style>
    </div>
  );
}
